import {useSearchParams, type To} from '@github-ui/react-router'
import {pullRequestsOverviewIndexRoute} from '../../../routes/overview-route'
import {issueCommentAnchor, reviewSummaryAnchor} from '../../comments/utils/comment-anchors'
import {ViewMode, ViewModeSchema} from '../../comments/utils/view-mode'
import type {SetStateAction} from 'react'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useLayoutQuery} from '../../../page-data/loaders/use-layout-query'
import {COMMENTS_VIEW_MODE_SEARCH_PARAM, OVERVIEW_COMMENTS_ANCHOR_ID} from '../../../helpers/constants'

type PullRequestPathParams = Parameters<typeof pullRequestsOverviewIndexRoute.generatePath>[0]

function routeToItem(pathParams: PullRequestPathParams, anchor: string): To {
  return {
    pathname: pullRequestsOverviewIndexRoute.generatePath(pathParams),
    hash: anchor,
    // Empty search resets the issues view mode to go back to "all", ensuring the comment is visible
    search: '',
  }
}

/** Build router `To` object for linking to an issue comment on the Overview page. */
export function routeToIssueComment(pathParams: PullRequestPathParams, commentId: string) {
  return routeToItem(pathParams, issueCommentAnchor(commentId))
}

/** Build router `To` object for linking to a review summary on the Overview page. */
export function routeToReviewSummary(pathParams: PullRequestPathParams, reviewId: string) {
  return routeToItem(pathParams, reviewSummaryAnchor(reviewId))
}

export function routeToCommentsSection(pathParams: PullRequestPathParams, viewMode: ViewMode): To {
  return {
    pathname: pullRequestsOverviewIndexRoute.generatePath(pathParams),
    hash: OVERVIEW_COMMENTS_ANCHOR_ID,
    search: `${COMMENTS_VIEW_MODE_SEARCH_PARAM}=${viewMode}`,
  }
}

const ViewModeValidator = Compile(ViewModeSchema)

function parseViewModeOrNull(value: string | null) {
  return value !== null && ViewModeValidator.Check(value) ? value : null
}

/** Get the view mode state and setter, using URL query params as the source of truth. */
export function useViewModeFromSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const viewModeParam = searchParams.get(COMMENTS_VIEW_MODE_SEARCH_PARAM)
  const viewMode = parseViewModeOrNull(viewModeParam) ?? ViewMode.IssueComments

  const setViewMode = (newViewMode: SetStateAction<ViewMode>) =>
    setSearchParams(
      params => {
        // We use no param to represent the "all" view. This keeps the page URL tidy
        if (newViewMode === ViewMode.IssueComments) params.delete(COMMENTS_VIEW_MODE_SEARCH_PARAM)
        else
          params.set(
            COMMENTS_VIEW_MODE_SEARCH_PARAM,
            typeof newViewMode === 'function' ? newViewMode(viewMode) : newViewMode,
          )

        return params
      },
      {preventScrollReset: true, preventAutofocus: true},
    )

  return [viewMode, setViewMode] as const
}

export function useCurrentPathParams(): PullRequestPathParams {
  const {repository, pullRequest} = useLayoutQuery().data

  return {
    owner: repository.ownerLogin,
    repo: repository.name,
    pr_number: pullRequest.number.toString(),
  }
}
